import React, { useState, useEffect, useContext } from "react";
import { Link, useHistory } from "react-router-dom";
import { notify } from "../../util/notify";
import axios from "axios";
import { imageurl } from "../../App";
import {
  getGuestCartTotals,
  updateGuestCartQty,
  removeGuestCartItem,
  CART_UPDATED_EVENT,
  setStoreFreeShippingThreshold
} from "../../util/guestCartService";
import { addToWishlist } from "../../util/wishlistService";
import { getProductImageUrl, handleProductImageError, applyStoreTitle } from "../../util/storeLogoHelper";
import { loadStoreSettings } from "../../util/storeSettings";
import {
  FaTrashAlt,
  FaShoppingBag,
  FaArrowRight,
  FaShieldAlt,
  FaTruck,
  FaTag,
  FaHeart,
  FaCheckCircle,
  FaTimes
} from "react-icons/fa";
import CartRecommendations from "./CartRecommendations";
import { ActiveThemeContext } from "../../themes/themeCache";
import { cartDrawerSettings } from "../../themes/cartDrawerSettings";
import useCheckoutQuote from "../../hooks/useCheckoutQuote";

export default function Cart() {
  const activeTheme = useContext(ActiveThemeContext);
  const cartLook = cartDrawerSettings(activeTheme?.theme?.settings || {});
  const history = useHistory();
  const [cartData, setCartData] = useState(() => getGuestCartTotals());
  const [couponCode, setCouponCode] = useState("");
  const [couponError, setCouponError] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    applyStoreTitle(null, "Your Shopping Cart");

    loadStoreSettings().then((settings) => {
      if (settings && settings.freeShippingThreshold !== undefined) {
        setStoreFreeShippingThreshold(settings.freeShippingThreshold);
        setCartData(getGuestCartTotals(settings.freeShippingThreshold));
      }
    }).catch(() => {});

    const handleCartUpdate = () => {
      setCartData(getGuestCartTotals());
    };

    window.addEventListener(CART_UPDATED_EVENT, handleCartUpdate);
    return () => {
      window.removeEventListener(CART_UPDATED_EVENT, handleCartUpdate);
    };
  }, []);

  const handleDecrement = (item) => {
    updateGuestCartQty(item.id, item.qty - 1);
  };

  const handleIncrement = (item) => {
    updateGuestCartQty(item.id, item.qty + 1);
  };

  const handleDeleteItem = (productId) => {
    removeGuestCartItem(productId);
    notify("Removed from your cart", "info");
  };

  const handleSaveForLater = (e, item) => {
    e.preventDefault();
    addToWishlist({
      id: item.id,
      name: item.name,
      slug: item.slug,
      categorySlug: item.categorySlug,
      categoryName: item.categoryName,
      image: item.image,
      price: item.price,
    });
    removeGuestCartItem(item.id);
    notify("Saved to your wishlist");
  };

  // Coupon validation
  const handleApplyCoupon = (e) => {
    e.preventDefault();
    if (!couponCode.trim()) {
      setCouponError("Enter a promo code.");
      return;
    }

    setCouponError("");
    setCouponLoading(true);
    axios
      .post(`/api/validate-coupon`, {
        code: couponCode.trim(),
        cart_amount: cartData.subtotal,
      })
      .then((res) => {
        setCouponLoading(false);
        if (res.data.status === 200) {
          setAppliedCoupon(res.data.coupon);
          notify(res.data.message || "Promo code applied");
        } else {
          setCouponError(res.data.message || "This promo code is not valid.");
        }
      })
      .catch(() => {
        setCouponLoading(false);
        setCouponError("Could not check the promo code. Please try again.");
      });
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode("");
  };

  const { items, subtotal, originalTotal, totalSavings, remainingForFreeShipping, freeShippingProgress, freeShippingThreshold } = cartData;
  const couponDiscount = appliedCoupon ? Number(appliedCoupon.discount_amount || 0) : 0;
  // Bundle savings come from the server, the same way checkout prices them.
  const { quote } = useCheckoutQuote({ items, city: "", shippingMethod: "standard", couponCode: null });
  const bundleSavings = quote?.bundles?.applied || [];
  const bundleDiscount = Number(quote?.bundles?.discount || 0);
  const grandTotal = Math.max(0, subtotal - couponDiscount - bundleDiscount);
  const totalCombinedSavings = totalSavings + couponDiscount;

  return (
    <div>
      {/* Header Banner & Breadcrumbs */}
      <div className="page-header-modern">
        <div className="container">
          <ul className="breadcrumb-modern">
            <li>
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-separator">/</li>
            <li className="active">Shopping Cart</li>
          </ul>
          <h1 className="page-header-title">Your Cart ({items.length})</h1>
        </div>
      </div>

      <div className="container py-4">
        {items.length === 0 ? (
          <div className="empty-state-modern py-5">
            <div className="empty-state-icon">
              <FaShoppingBag />
            </div>
            <h3 className="fw-bold mb-2">Your Shopping Cart is Empty</h3>
            <p className="text-muted mb-4">
              Looks like you haven't added any items to your cart yet.
            </p>
            <Link
              to="/collection"
              className="btn btn-primary rounded-pill px-4 py-3 fw-bold"
              style={{ background: "var(--accent)", border: "none" }}
            >
              Explore Collections <FaArrowRight size={13} className="ms-1" />
            </Link>
          </div>
        ) : (
          <div className="row g-4">
            {/* Left: Cart Items List */}
            <div className="col-12 col-lg-8">
              {/* Free Shipping Progress Indicator */}
              <div className="p-3 bg-white border rounded-3 mb-4" style={{ boxShadow: "var(--shadow-xs)" }}>
                <div className="d-flex justify-content-between align-items-center mb-2" style={{ fontSize: "13.5px" }}>
                  <span className="fw-semibold d-flex align-items-center gap-2">
                    <FaTruck color="var(--accent)" />
                    {freeShippingThreshold === 0 ? (
                      <span className="text-success fw-bold">🎉 FREE Nationwide Delivery on All Orders!</span>
                    ) : remainingForFreeShipping === 0 ? (
                      <span className="text-success fw-bold">🎉 You qualified for FREE Nationwide Delivery!</span>
                    ) : (
                      <span>Add <strong>Rs. {Number(remainingForFreeShipping).toLocaleString()}</strong> more to get FREE delivery (Orders over Rs. {Number(freeShippingThreshold).toLocaleString()})</span>
                    )}
                  </span>
                  <span className="fw-bold">{Math.round(freeShippingProgress)}%</span>
                </div>
                <div className="progress" style={{ height: "8px", borderRadius: "4px" }}>
                  <div
                    className="progress-bar progress-bar-striped progress-bar-animated bg-success"
                    style={{ width: `${freeShippingProgress}%` }}
                  />
                </div>
              </div>

              <div className="cart-table-card">
                <div className="d-flex justify-content-between align-items-center pb-3 mb-2 border-bottom">
                  <h5 className="fw-bold m-0">Items in Cart ({items.length})</h5>
                  <Link to="/collection" className="text-muted" style={{ fontSize: "13.5px" }}>
                    + Continue Shopping
                  </Link>
                </div>

                {items.map((item) => (
                  <div className="cart-item-row" key={item.id}>
                    {/* Item Thumbnail */}
                    <div className="cart-item-img">
                      <img
                        src={getProductImageUrl(item.image)}
                        alt={item.name}
                        onError={handleProductImageError}
                      />
                    </div>

                    {/* Details */}
                    <div className="cart-item-details">
                      <h4 className="cart-item-title">{item.name}</h4>
                      <div className="d-flex align-items-baseline gap-2 mb-2">
                        <span className="cart-item-price">
                          Rs. {Number(item.price).toLocaleString()}
                        </span>
                        {item.hasDiscount && (
                          <span
                            style={{
                              fontSize: "12.5px",
                              color: "var(--text-muted)",
                              textDecoration: "line-through",
                            }}
                          >
                            Rs. {Number(item.originalPrice).toLocaleString()}
                          </span>
                        )}
                      </div>

                      {/* Quantity Stepper & Actions */}
                      <div className="d-flex align-items-center gap-3 mt-2 flex-wrap">
                        <div className="qty-stepper" style={{ height: "36px" }}>
                          <button
                            type="button"
                            className="qty-btn"
                            style={{ width: "34px", fontSize: "15px" }}
                            onClick={() => handleDecrement(item)}
                          >
                            -
                          </button>
                          <div className="qty-value" style={{ width: "34px", fontSize: "14px" }}>
                            {item.qty}
                          </div>
                          <button
                            type="button"
                            className="qty-btn"
                            style={{ width: "34px", fontSize: "15px" }}
                            onClick={() => handleIncrement(item)}
                          >
                            +
                          </button>
                        </div>

                        <button
                          type="button"
                          onClick={(e) => handleSaveForLater(e, item)}
                          className="btn btn-link p-0 text-decoration-none d-flex align-items-center gap-1 text-muted"
                          style={{ fontSize: "12.5px" }}
                        >
                          <FaHeart size={12} /> Save for later
                        </button>

                        <button
                          type="button"
                          onClick={() => handleDeleteItem(item.id)}
                          className="cart-item-remove-btn"
                        >
                          <FaTrashAlt size={13} /> Remove
                        </button>
                      </div>
                    </div>

                    {/* Total for line */}
                    <div className="text-end d-none d-sm-block">
                      <span className="fw-bold" style={{ fontSize: "16px" }}>
                        Rs. {Number(item.price * item.qty).toLocaleString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              {cartLook.showRecommendations && (
                <CartRecommendations items={items} title={cartLook.recommendationsTitle} />
              )}
            </div>

            {/* Right: Order Summary */}
            <div className="col-12 col-lg-4">
              <div className="order-summary-card">
                <h5 className="fw-bold mb-3">Order Summary</h5>

                {/* Promo Code Input */}
                <div className="mb-3">
                  {!appliedCoupon ? (
                    <form onSubmit={handleApplyCoupon} className="d-flex gap-2">
                      <input
                        type="text"
                        placeholder="Promo code (e.g. WELCOME10)"
                        value={couponCode}
                        onChange={(e) => {
                          setCouponCode(e.target.value);
                          setCouponError("");
                        }}
                        aria-invalid={couponError ? "true" : "false"}
                        aria-describedby="cart-coupon-error"
                        className={`form-control form-control-sm ${couponError ? "is-invalid" : ""}`}
                        style={{ borderRadius: "var(--radius-md)", fontSize: "13px" }}
                      />
                      <button
                        type="submit"
                        className="btn btn-dark btn-sm px-3 rounded-3"
                        style={{ fontSize: "13px" }}
                        disabled={couponLoading}
                      >
                        {couponLoading ? "..." : "Apply"}
                      </button>
                    </form>
                  ) : null}
                  {!appliedCoupon && couponError && (
                    <small id="cart-coupon-error" className="text-danger d-block mt-1" role="alert">{couponError}</small>
                  )}
                  {!appliedCoupon ? null : (
                    <div className="p-2 border rounded-3 bg-light d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center gap-2">
                        <FaTag color="var(--success)" />
                        <span className="fw-bold" style={{ fontSize: "13px" }}>
                          {appliedCoupon.code}
                        </span>
                        <span className="badge bg-success">Applied</span>
                      </div>
                      <button
                        type="button"
                        onClick={handleRemoveCoupon}
                        className="btn btn-link text-danger p-0"
                        title="Remove coupon"
                      >
                        <FaTimes size={13} />
                      </button>
                    </div>
                  )}
                </div>

                <div className="summary-row">
                  <span>Subtotal</span>
                  <span>Rs. {Number(originalTotal).toLocaleString()}</span>
                </div>

                {totalCombinedSavings > 0 && (
                  <div className="summary-row text-success fw-semibold">
                    <span>Discount Savings</span>
                    <span>-Rs. {Number(totalCombinedSavings).toLocaleString()}</span>
                  </div>
                )}

                {bundleSavings.map((bundle) => (
                  <div key={bundle.id} className="summary-row text-success fw-semibold">
                    <span>Bundle · {bundle.name}{bundle.sets > 1 ? ` ×${bundle.sets}` : ""}</span>
                    <span>-Rs. {Number(bundle.discount).toLocaleString()}</span>
                  </div>
                ))}

                <div className="summary-row">
                  <span>Delivery Estimate</span>
                  <span className="text-success fw-semibold">
                    {remainingForFreeShipping === 0 ? "Free Nationwide Delivery" : "Standard Delivery"}
                  </span>
                </div>

                <div className="summary-row total-row">
                  <span>Estimated Total</span>
                  <span style={{ color: "var(--accent)" }}>
                    Rs. {Number(grandTotal).toLocaleString()}
                  </span>
                </div>

                <button
                  type="button"
                  onClick={() => history.push("/checkout")}
                  className="btn-checkout-cta w-100 text-center text-decoration-none"
                >
                  Proceed to Checkout <FaArrowRight size={14} />
                </button>

                <div className="mt-4 pt-3 border-top d-flex flex-column gap-2 text-muted" style={{ fontSize: "12px" }}>
                  <div className="d-flex align-items-center gap-2">
                    <FaCheckCircle color="var(--success)" />
                    <span>
                      {freeShippingThreshold === 0
                        ? "Free nationwide delivery on all orders"
                        : `Free shipping on all orders over Rs. ${Number(freeShippingThreshold || 2500).toLocaleString()}`}
                    </span>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <FaShieldAlt color="var(--accent)" />
                    <span>256-Bit SSL Encrypted & Protected</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
